import { useEffect, useMemo, useState } from "react";
import { api, apiErrToStr, fmtDate } from "@/lib/api";
import { Plus, Trash2, Edit3, X, Save, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Generic CRUD page.
 * fields: [{ key, label, type: text|number|select|toggle|textarea, options?: [{value,label}], mono?, placeholder? }]
 * renderCell(row, key) -> optional custom render
 */
export default function CrudPage({
  title, subtitle, endpoint, fields, defaultValues, testidPrefix,
  renderRow, headers, allowDelete = true, canEdit = () => true, canDelete = () => true,
  transformFormOpen, transformSubmit, refreshInterval = 0, renderActions,
}) {
  const { user, activeTenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [error, setError] = useState("");
  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(endpoint, isSuperadmin ? { skipTenantScope: true } : undefined);
      setRows(data);
    } finally { if (!silent) setLoading(false); }
  };
  useEffect(() => {
    load();
    if (!refreshInterval) return undefined;
    const timer = setInterval(() => load(true), refreshInterval);
    return () => clearInterval(timer);
    /* eslint-disable-next-line */
  }, [endpoint, refreshInterval, isSuperadmin]);

  const scopedRows = useMemo(() => {
    if (!isSuperadmin || !activeTenantId) return rows;
    return rows.filter((row) => {
      if (!row || typeof row !== "object") return false;
      if (!Object.prototype.hasOwnProperty.call(row, "tenant_id")) return true;
      return String(row.tenant_id || "") === String(activeTenantId) || row.universal === true;
    });
  }, [activeTenantId, isSuperadmin, rows]);

  const open = (row = null) => {
    setError("");
    setEditing(row);
    const nextForm = row ? { ...defaultValues, ...row } : { ...defaultValues };
    setForm(transformFormOpen ? transformFormOpen(nextForm, row) : nextForm);
    setShowForm(true);
  };
  const close = () => { setShowForm(false); setEditing(null); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = transformSubmit ? transformSubmit(form, editing) : form;
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      close();
      await load();
      window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Confirma exclusão?")) return;
    await api.delete(`${endpoint}/${id}`);
    await load();
  };

  const uploadFile = async (f, file) => {
    if (!file) return;
    setUploading(f.key); setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post(f.uploadEndpoint, data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((current) => ({ ...current, [f.targetKey || f.key]: res.data.path || "" }));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally { setUploading(""); }
  };

  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{subtitle}</div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        </div>
        <button data-testid={`${testidPrefix}-add-btn`} className="sbc-btn sbc-btn-primary" onClick={() => open()}>
          <Plus size={14} /> Adicionar
        </button>
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="overflow-auto max-h-[70vh]">
          <table className="sbc-table" data-testid={`${testidPrefix}-table`}>
            <thead>
              <tr>
                {headers.map((h) => <th key={h}>{h}</th>)}
                <th className="w-24 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length + 1} className="text-center text-[color:var(--text-muted)] py-8">Carregando…</td></tr>
              ) : scopedRows.length === 0 ? (
                <tr><td colSpan={headers.length + 1} className="text-center text-[color:var(--text-muted)] py-8">Nenhum registro.</td></tr>
              ) : scopedRows.map((row) => {
                const rowCanEdit = canEdit(row);
                const rowCanDelete = allowDelete && canDelete(row);
                return <tr key={row.id} data-testid={`${testidPrefix}-row-${row.id}`}>
                  {renderRow(row)}
                  <td className="text-right">
                    <div className="inline-flex gap-1 items-center">
                      {typeof renderActions === "function" && renderActions(row, { reload: load })}
                      {rowCanEdit && <button data-testid={`${testidPrefix}-edit-${row.id}`} className="sbc-btn" onClick={() => open(row)} title="Editar"><Edit3 size={12} /></button>}
                      {rowCanDelete && <button data-testid={`${testidPrefix}-delete-${row.id}`} className="sbc-btn sbc-btn-danger" onClick={() => del(row.id)} title="Excluir"><Trash2 size={12} /></button>}
                      {!rowCanEdit && !rowCanDelete && <span className="text-[10px] uppercase text-[color:var(--text-muted)]">Somente leitura</span>}
                    </div>
                  </td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="sbc-card w-full max-w-2xl p-6 fade-in" onClick={(e) => e.stopPropagation()} data-testid={`${testidPrefix}-form`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{editing ? "Editar" : "Novo"}</div>
                <h2 className="text-lg font-semibold">{title}</h2>
              </div>
              <button className="sbc-btn" onClick={close} data-testid={`${testidPrefix}-form-close`}><X size={14} /></button>
            </div>

            <form onSubmit={save} className="grid grid-cols-2 gap-4">
              {fields.filter((f) => !(typeof f.hidden === "function" ? f.hidden(form) : f.hidden)).map((f) => (
                <div key={f.key} className={f.full ? "col-span-2" : "col-span-2 md:col-span-1"}>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] font-semibold block mb-1.5">{f.label}</label>
                  {f.type === "select" ? (
                    <select data-testid={`${testidPrefix}-field-${f.key}`} className={`sbc-input ${f.mono ? "font-mono" : ""}`} value={form[f.key] ?? ""} onChange={(e) => {
                      const next = { ...form, [f.key]: e.target.value };
                      setForm(f.onChange ? f.onChange(next, e.target.value, form) : next);
                    }}>
                      {(typeof f.options === "function" ? f.options(form) : (f.options || [])).map((o) => <option key={o.value} value={o.value} className="bg-[color:var(--bg-surface)]">{o.label}</option>)}
                    </select>
                  ) : f.type === "textarea" ? (
                    <textarea data-testid={`${testidPrefix}-field-${f.key}`} className={`sbc-input min-h-[112px] ${f.mono ? "font-mono" : ""}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ""} rows={f.rows || 4} />
                  ) : f.type === "file" ? (
                    <div className="space-y-2">
                      <input
                        data-testid={`${testidPrefix}-field-${f.key}`}
                        type="file"
                        accept={f.accept || ""}
                        className="sbc-input"
                        disabled={uploading === f.key}
                        onChange={(e) => uploadFile(f, e.target.files?.[0])}
                      />
                      {uploading === f.key && <div className="text-[10px] text-[color:var(--text-muted)] font-mono">enviando arquivo...</div>}
                      {(form[f.targetKey || f.key]) && <div className="text-[10px] text-[color:var(--accent-green)] font-mono break-all">{form[f.targetKey || f.key]}</div>}
                    </div>
                  ) : f.type === "checkbox-list" ? (
                    <div className="max-h-56 space-y-2 overflow-auto rounded-sm border border-[color:var(--border-color)] bg-[color:var(--bg-panel)] px-3 py-3">
                      {(typeof f.options === "function" ? f.options(form) : (f.options || [])).map((o) => {
                        const selected = Array.isArray(form[f.key]) ? form[f.key] : [];
                        const checked = selected.includes(o.value);
                        return (
                          <label key={o.value} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const current = Array.isArray(form[f.key]) ? form[f.key] : [];
                                const nextValues = e.target.checked
                                  ? [...current, o.value]
                                  : current.filter((value) => value !== o.value);
                                setForm({ ...form, [f.key]: nextValues });
                              }}
                              className="mt-0.5 h-4 w-4"
                            />
                            <span className="flex flex-col">
                              <span className={f.mono ? "font-mono" : ""}>{o.label}</span>
                              {o.hint && <span className="font-mono text-[10px] text-[color:var(--text-muted)]">{o.hint}</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : f.type === "toggle" ? (
                    <label className="flex items-center gap-2 pt-1.5">
                      <input data-testid={`${testidPrefix}-field-${f.key}`} type="checkbox" checked={!!form[f.key]} onChange={(e) => {
                        const next = { ...form, [f.key]: e.target.checked };
                        setForm(f.onChange ? f.onChange(next, e.target.checked, form) : next);
                      }} className="w-4 h-4" />
                      <span className="text-sm text-[color:var(--text-secondary)]">{form[f.key] ? "Ativado" : "Desativado"}</span>
                    </label>
                  ) : (
                    <input data-testid={`${testidPrefix}-field-${f.key}`} type={f.type || "text"} className={`sbc-input ${f.mono ? "font-mono" : ""}`} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })} placeholder={f.placeholder || ""} />
                  )}
                  {f.hint && <div className="text-[10px] text-[color:var(--text-muted)] mt-1 font-mono">{typeof f.hint === "function" ? f.hint(form) : f.hint}</div>}
                </div>
              ))}
              {error && <div className="col-span-2 text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div>}
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" className="sbc-btn" onClick={close} data-testid={`${testidPrefix}-form-cancel`}>Cancelar</button>
                <button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving} data-testid={`${testidPrefix}-form-save`}>
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export { fmtDate };
